'use client';

import { forwardRef, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import {
  type Country,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
  parsePhoneNumber,
  // `react-phone-number-input`'s bare entry point resolves to its MIN metadata bundle (smaller,
  // but validation is length/leading-digit only for many countries — e.g. it reports a US-shaped
  // number like "2015550123" as a VALID Indian number). `/max` carries the full per-country
  // pattern data (same bundle `libphonenumber-js`'s own default entry already uses, which is what
  // `AsYouType` below is built on), so validity actually reflects the selected country's real format.
} from 'react-phone-number-input/max';
import { AsYouType } from 'libphonenumber-js';
import en from 'react-phone-number-input/locale/en.json';
import flagsImport from 'react-phone-number-input/flags';
import { SearchBox } from './SearchBox';

/**
 * Per-country completeness check for an E.164 phone string (libphonenumber metadata). An empty / falsy
 * value is treated as "complete" (no error) — required-ness is a separate form concern. Used by the
 * guest-form zod schema + inline error.
 */
export function isPhoneComplete(value: string | undefined | null): boolean {
  if (!value) return true;
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

/**
 * Looser variant — passes when the number could still be valid for its country at its current length.
 * Used to decide whether to surface an inline error *while typing* (only flag once no longer "possible").
 */
export function isPhonePossible(value: string | undefined | null): boolean {
  if (!value) return true;
  try {
    return isPossiblePhoneNumber(value);
  } catch {
    return false;
  }
}

interface PhoneInputProps {
  id?: string;
  name?: string;
  /** E.164 value (e.g. "+447700900123"). The text box shows only the NATIONAL part; the +CC lives in the picker. */
  value?: string;
  defaultValue?: string;
  /** Initial country when uncontrolled. */
  defaultCountry?: Country;
  /** Controlled selected country (ISD). When provided the parent owns it (auto-syncs the ISD to the address country). */
  country?: Country;
  /** Fired when the selected country changes (picker click OR a typed +CC resolving to a country). */
  onCountryChange?: (country: Country) => void;
  onChange?: (value: string | undefined) => void;
  /** Fired with the per-country validity of the current value (`true` for empty). */
  onValidityChange?: (valid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
  /** When `true`, render a built-in inline error below the field for an impossible number. */
  showInlineError?: boolean;
  errorMessage?: string;
}

interface CountrySelectComponentProps {
  value: Country;
  onChange: (country: Country) => void;
  options: Array<{ value?: Country; label: string }>;
  iconComponent: ComponentType<{ country: Country }>;
  disabled?: boolean;
}

// ── libphonenumber helpers ──────────────────────────────────────────────────

const flags = flagsImport as unknown as Record<string, ComponentType<{ title?: string }>>;
const LABELS = en as Record<string, string>;
/** All supported countries with display labels — feeds the picker dropdown. */
const COUNTRY_OPTIONS: Array<{ value?: Country; label: string }> = getCountries().map((c) => ({
  value: c,
  label: LABELS[c] || c,
}));

/** Map a calling code (e.g. "44") to its PRIMARY country (first match — GB for 44, US for 1). */
function callingCodeToCountry(cc?: string): Country | undefined {
  if (!cc) return undefined;
  for (const c of getCountries()) {
    try {
      if (getCountryCallingCode(c) === cc) return c;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

/**
 * libphonenumber's `formatNational()` prepends the national trunk-call prefix — "0" for the UK, India,
 * and most of the world (e.g. "+447700900123" → "07700 900123", "+919087654321" → "090876 54321").
 * For a GLOBAL audience that leading 0 confuses users (esp. India's 10-digit mobiles), so we hide it in
 * the UI. This is DISPLAY-ONLY: the stored value stays full E.164, and parsing/validation/country
 * resolution are unchanged (a national significant number never begins with 0, so a leading 0 is always
 * the trunk prefix → safe to drop, and `AsYouType` re-parses the prefix-less text back to the same E.164).
 */
function stripTrunkPrefix(national: string): string {
  return national.replace(/^0[\s-]?/, '');
}

/** E.164 → NATIONAL display string WITHOUT the trunk prefix (e.g. "+447700900123" → "7700 900123"). Never shows the +CC. */
function toNationalDisplay(value: string | undefined, country: Country): string {
  if (!value) return '';
  try {
    const pn = parsePhoneNumber(value);
    if (pn) return stripTrunkPrefix(pn.formatNational());
  } catch {
    /* fall through to manual strip */
  }
  // Incomplete / unparseable: strip the leading "+<callingCode>" for the current country, then format.
  try {
    const cc = getCountryCallingCode(country);
    if (value.startsWith(`+${cc}`)) {
      const national = value.slice(1 + cc.length);
      return stripTrunkPrefix(new AsYouType(country).input(national));
    }
  } catch {
    /* ignore */
  }
  return value.replace(/^\+/, '');
}

/** National text typed under a country → E.164 (handles the national trunk prefix, e.g. GB leading 0). */
function nationalToE164(text: string, country: Country): string {
  const ayt = new AsYouType(country);
  ayt.input(text);
  return ayt.getNumberValue() || '';
}

/**
 * Custom country picker shown to the left of the phone input — flag + dial code (e.g. "🇬🇧 +44") with a
 * searchable dropdown. This is the single home of the ISD code; it never appears in the number box.
 */
function CountryDialPicker({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled,
}: CountrySelectComponentProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !!o.value)
      .map((o) => {
        const code = o.value as Country;
        let dial = '';
        try {
          dial = `+${getCountryCallingCode(code)}`;
        } catch {
          dial = '';
        }
        return { code, label: o.label, dial };
      })
      .filter((o) => {
        if (!q) return true;
        return o.label.toLowerCase().includes(q) || o.code.toLowerCase().includes(q) || o.dial.includes(q);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const currentDial = (() => {
    try {
      return value ? `+${getCountryCallingCode(value)}` : '';
    } catch {
      return '';
    }
  })();

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 bg-purple-light/40 rounded-l-xl border-r border-border-light h-full text-sm text-secondary hover:bg-purple-light/70 disabled:opacity-60"
      >
        {value && (
          <span className="inline-flex items-center shrink-0 [&>img]:w-5 [&>img]:h-auto [&>img]:block [&>svg]:w-5 [&>svg]:h-auto [&>svg]:block">
            <Icon country={value} />
          </span>
        )}
        {currentDial && <span className="font-semibold tabular-nums leading-none">{currentDial}</span>}
        <i className={`fa-solid fa-chevron-down text-xs text-info transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-30 mt-1 left-0 w-72 bg-white border border-border-light rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-2 border-b border-border-light">
            <SearchBox value={query} onChange={setQuery} placeholder="Search country or +code…" autoFocus />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-info text-center">No matches</div>
            ) : (
              filtered.map((o) => {
                const isSelected = o.code === value;
                return (
                  <div
                    key={o.code}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(o.code);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={`flex items-center gap-3 px-3 py-2 text-base cursor-pointer hover:bg-purple-light ${
                      isSelected ? 'bg-purple-light/60 font-semibold text-primary' : 'text-secondary'
                    }`}
                  >
                    <span className="inline-flex items-center shrink-0 [&>img]:w-5 [&>img]:block [&>svg]:w-5 [&>svg]:block">
                      <Icon country={o.code} />
                    </span>
                    <span className="flex-1">{o.label}</span>
                    <span className="text-info text-xs tabular-nums">{o.dial}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FlagIcon({ country }: { country: Country }) {
  const F = flags[country];
  return F ? <F title={LABELS[country] || country} /> : <span className="text-xs">{country}</span>;
}

/**
 * Phone input where the **ISD code lives ONLY in the country picker** and never in the number box.
 *
 * - Value is stored as E.164 (e.g. "+447700900123") — round-trips with the CMS (intl-tel-input stores
 *   the same `+CC` + national format).
 * - The text box shows the NATIONAL number (e.g. "07700 900123"); the `+CC` shows in the picker pill.
 * - If the user types/pastes a `+CC` number, it is parsed: the picker switches to that country and the
 *   `+CC` is stripped from the box (the national part remains). Typed national digits format as E.164
 *   under the selected country (handling the national trunk prefix, e.g. GB leading 0).
 * - `country`/`onCountryChange` let a parent control the selected country (e.g. auto-sync the ISD to the
 *   address country while the number is still blank — see `Step1Identity`).
 *
 * Built on a custom input (NOT `react-phone-number-input`'s, which renders controlled E.164 values in
 * international format — the +CC would show in the box) over `libphonenumber-js` `AsYouType`.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    id,
    name,
    value,
    defaultValue,
    defaultCountry = 'GB',
    country: controlledCountry,
    onCountryChange,
    onChange,
    onValidityChange,
    placeholder = 'Mobile number',
    required,
    disabled,
    className,
    hasError = false,
    showInlineError = false,
    errorMessage = 'Please enter a valid phone number for the selected country',
  },
  ref,
) {
  const isControlledCountry = controlledCountry !== undefined;
  const [internalCountry, setInternalCountry] = useState<Country>(defaultCountry);
  const selectedCountry = controlledCountry ?? internalCountry;

  const setCountry = (c: Country) => {
    if (!isControlledCountry) setInternalCountry(c);
    onCountryChange?.(c);
  };

  const effectiveValue = value ?? defaultValue ?? '';
  const [text, setText] = useState<string>(() => toNationalDisplay(effectiveValue, selectedCountry));
  const focusedRef = useRef(false);

  // Keep the box in sync when the value or country changes EXTERNALLY (prefill, ISD auto-sync) — but never
  // clobber what the user is mid-typing. When the country is UNCONTROLLED, also match the picker to the
  // value's OWN country (so a prefilled foreign number shows its real flag/ISD, not the default).
  useEffect(() => {
    if (focusedRef.current) return;
    if (!isControlledCountry && effectiveValue) {
      try {
        const c = parsePhoneNumber(effectiveValue)?.country;
        if (c && c !== internalCountry) {
          setInternalCountry(c);
          return; // re-runs with the corrected country
        }
      } catch {
        /* incomplete number — keep the current picker country */
      }
    }
    setText(toNationalDisplay(effectiveValue, selectedCountry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveValue, selectedCountry]);

  const [touched, setTouched] = useState(false);
  const inlineInvalid = showInlineError && touched && !!value && !isPhonePossible(value);
  const showRedBorder = hasError || inlineInvalid;
  const borderCls = showRedBorder ? 'border-red-300' : 'border-[#ced4da]';

  const emit = (e164: string) => {
    onChange?.(e164);
    onValidityChange?.(isPhoneComplete(e164));
  };

  const handleInput = (raw: string) => {
    if (raw.trim().startsWith('+')) {
      // International / ISD typed or pasted → resolve the country, strip the +CC into the picker.
      const ayt = new AsYouType();
      ayt.input(raw);
      const num = ayt.getNumber();
      const resolved = ayt.country ?? callingCodeToCountry(num?.countryCallingCode) ?? selectedCountry;
      const e164 = ayt.getNumberValue() || '';
      if (resolved !== selectedCountry) setCountry(resolved);
      setText(toNationalDisplay(e164, resolved));
      emit(e164);
      return;
    }
    // National input under the selected country. Keep the raw text (no mid-type reformat → no caret jump);
    // pretty-format happens on blur.
    setText(raw);
    emit(nationalToE164(raw, selectedCountry));
  };

  // Picker change: re-interpret the current national digits under the new country.
  const handleCountryPick = (c: Country) => {
    setCountry(c);
    const e164 = text ? nationalToE164(text, c) : '';
    setText(toNationalDisplay(e164, c));
    emit(e164);
  };

  return (
    <div>
      <div
        className={
          className ||
          `bip-phone flex items-stretch border rounded-md bg-white transition-colors ${borderCls}`
        }
      >
        <CountryDialPicker
          value={selectedCountry}
          onChange={handleCountryPick}
          options={COUNTRY_OPTIONS}
          iconComponent={FlagIcon}
          disabled={disabled}
        />
        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            setTouched(true);
            // Pretty-format the national display once editing ends.
            setText(toNationalDisplay(value ?? '', selectedCountry));
          }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-invalid={showRedBorder || undefined}
          className="flex-1 border-0 bg-transparent outline-none text-sm pl-0 pr-3 py-2 rounded-r-xl"
        />
      </div>
      {inlineInvalid && <p className="text-xs text-red-600 mt-1">{errorMessage}</p>}
    </div>
  );
});
